import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, Table, Badge, OverlayTrigger, Tooltip as BootstrapTooltip, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInfoCircle, faWallet, faSync, faCheckCircle, faClock, faSave, 
  faBuilding, faExchangeAlt, faBolt
} from '@fortawesome/free-solid-svg-icons';
import { dashboardService, categoryService, recordService } from '../../services/api';
import { Category, AccountType, RecordType, Currency } from '../../types';
import MonthlySummaryFilters from './MonthlySummaryFilters';
import '../../styles/Records.css';
import '../../styles/Records-mobile.css';
import '../../styles/MonthlySummary.css';

// Define interfaces for the monthly summary data
interface MonthlySummaryItem {
  month: string;
  month_name: string;
  category_id: number;
  record_type: RecordType;
  amount_vnd: number;
  amount_usd: number;
  tax_vnd: number;
  tax_usd: number;
  record_count: number;
  category: {
    name: string;
    type: string;
    account_type: AccountType;
  };
}

// Interface for filters
interface FiltersType {
  startDate: Date;
  endDate: Date;
  recordType: RecordType | '';
  accountType: AccountType | '';
  categoryId: number[];
  sortBy: string;
  sortOrder: string;
  amountCurrency: Currency;
}

const MonthlySummary: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for summary data
  const [summaryData, setSummaryData] = useState<MonthlySummaryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);
  
  // State for supporting data
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Helper function to get current year date range
  const getCurrentYearRange = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    return { startOfYear, now };
  };

  // State for filters - initialize with current year as default
  const [filters, setFilters] = useState<FiltersType>(() => {
    const { startOfYear, now } = getCurrentYearRange();
    return {
      startDate: startOfYear,
      endDate: now,
      recordType: '' as RecordType | '',
      accountType: 'company' as AccountType | '',
      categoryId: [] as number[],
      sortBy: 'month',
      sortOrder: 'desc', // Default to descending order
      amountCurrency: 'vnd' as Currency
    };
  });
  
  // State for current balance
  const [currentBalance, setCurrentBalance] = useState<{ total_vnd: number, total_usd: number }>({ total_vnd: 0, total_usd: 0 });
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);

  // Initialize data
  const initializeData = async () => {
    setLoading(true);
    
    try {
      // Fetch categories
      const categoriesResult = await categoryService.getAll();
      const categoriesData = categoriesResult.data?.data || categoriesResult.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      
      // Fetch current balance
      await fetchCurrentBalance();
      
      // Fetch monthly summary data
      await fetchMonthlySummary();
      
      setInitialized(true);
    } catch (error) {
      console.error('Error initializing data:', error);
    }
  };
  
  // Fetch current balance
  const fetchCurrentBalance = async (forceRefresh = false) => {
    setBalanceLoading(true);
    try {
      const params = {
        accountType: filters.accountType,
        forceRefresh: forceRefresh
      };
      const response = await recordService.getCurrentBalance(params);
      setCurrentBalance(response.data);
    } catch (error) {
      console.error('Error fetching current balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Effect to initialize data on component mount
  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to fetch monthly summary when filters change (but only after initialization)
  useEffect(() => {
    if (initialized) {
      fetchMonthlySummary();
    }
  }, [
    filters.startDate, 
    filters.endDate, 
    filters.categoryId, 
    filters.recordType,
    filters.accountType,
    initialized
  ]);
  
  // Effect to fetch current balance when account type changes
  useEffect(() => {
    if (initialized) {
      fetchCurrentBalance();
    }
  }, [filters.accountType, initialized]);

  // Fetch monthly summary data
  const fetchMonthlySummary = async () => {
    setLoading(true);
    
    try {
      const categoryIdParam = filters.categoryId && filters.categoryId.length > 0 
        ? filters.categoryId.join(',') 
        : '';
      
      const params: any = {
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        accountType: filters.accountType,
        categoryId: categoryIdParam,
        recordType: filters.recordType || ''
      };

      // Add date filters if present
      if (filters.startDate) params.startDate = format(filters.startDate, 'yyyy-MM-dd');
      if (filters.endDate) params.endDate = format(filters.endDate, 'yyyy-MM-dd');
      
      const response = await dashboardService.getMonthlySummary(params);
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleSearch = () => {
    fetchMonthlySummary();
  };

  const handleResetFilters = () => {
    const { startOfYear, now } = getCurrentYearRange();
    
    const resetFilters = {
      startDate: startOfYear,
      endDate: now,
      recordType: '' as RecordType | '',
      accountType: 'company' as AccountType | '',
      categoryId: [] as number[],
      sortBy: 'month',
      sortOrder: 'desc',
      amountCurrency: 'vnd' as Currency
    };
    
    setFilters(resetFilters);
    fetchMonthlySummary();
  };

  // Navigate to records page with filter for the selected month
  const handleViewDetails = (month: string) => {
    // Parse the month string (YYYY-MM format)
    const year = parseInt(month.substring(0, 4));
    const monthIndex = parseInt(month.substring(5, 7)) - 1; // 0-based month index
    
    // Create date range for the selected month
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0); // Last day of month
    
    // Navigate to records page with filters
    navigate('/', {
      state: {
        filters: {
          startDate,
          endDate,
          accountType: filters.accountType,
          recordType: filters.recordType,
          categoryId: filters.categoryId.length > 0 ? filters.categoryId : undefined
        }
      }
    });
  };

  // Format currency values
  const formatCurrency = (value: number, currency: 'vnd' | 'usd') => {
    if (currency === 'vnd') {
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' đ';
    } else {
      return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
  };

  // Format dual currency display
  const formatDualCurrency = (vndValue: number, usdValue: number, selectedCurrency: 'vnd' | 'usd', recordType: RecordType) => {
    // Determine if the value is negative (expense)
    const isNegative = vndValue < 0;
    
    // Format the values with proper sign
    const formattedVnd = formatCurrency(Math.abs(vndValue), 'vnd');
    const formattedUsd = usdValue.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    
    // Apply negative sign if needed
    const displayVnd = isNegative ? `-${formattedVnd}` : formattedVnd;
    const displayUsd = isNegative ? `-$${formattedUsd}` : `$${formattedUsd}`;
    
    // Determine text color class based on record type
    const colorClass = recordType === 'income' ? 'income-text' : 'expense-text';
    
    if (selectedCurrency === 'vnd') {
      return (
        <div>
          <div className={`primary-amount ${colorClass}`}>{displayVnd}</div>
          <div className="secondary-amount">{isNegative ? `-$${formattedUsd}` : `$${formattedUsd}`}</div>
        </div>
      );
    } else {
      return (
        <div>
          <div className={`primary-amount ${colorClass}`}>{displayUsd}</div>
          <div className="secondary-amount">{displayVnd}</div>
        </div>
      );
    }
  };

  // Group data by month for display
  const groupedByMonth = summaryData.reduce((acc, item) => {
    if (!acc[item.month]) {
      acc[item.month] = {
        month: item.month,
        month_name: item.month_name,
        items: []
      };
    }
    acc[item.month].items.push(item);
    return acc;
  }, {} as Record<string, { month: string, month_name: string, items: MonthlySummaryItem[] }>);

  // Convert to array and sort by month (always descending - latest month first)
  const monthlyData = Object.values(groupedByMonth).sort((a, b) => b.month.localeCompare(a.month));

  // Render table rows with proper month-based styling
  const renderTableRows = () => {
    let rows: JSX.Element[] = [];
    
    monthlyData.forEach((monthGroup, groupIndex) => {
      // Apply alternating background colors to months
      const monthClassName = groupIndex % 2 === 0 ? 'even-month' : 'odd-month';
      
      // Add rows for this month with the appropriate class
      const monthRows = monthGroup.items.map((item, index) => (
        <tr 
          key={`${item.month}-${item.category_id}-${index}`}
          className={monthClassName}
        >
              <td>
                {index === 0 || monthGroup.items[index - 1].category_id !== item.category_id ? (
                  <span>{item.month_name}</span>
                ) : null}
              </td>
              <td>
                <div className="category-cell">
                  <span 
                    className={`category-dot ${item.record_type === 'expense' ? 'category-dot-expense' : 'category-dot-income'}`}
                  ></span>
                  <span>{item.category.name}</span>
                </div>
              </td>
              <td>
                <Badge 
                  bg={item.record_type === 'expense' ? 'danger' : 'success'} 
                  className="record-type-badge"
                >
                  {item.record_type === 'expense' ? 'Expense' : 'Income'}
                </Badge>
              </td>
              <td className="amount-column">
                {formatDualCurrency(item.amount_vnd, item.amount_usd, filters.amountCurrency, item.record_type)}
              </td>
              <td className="tax-column">
                {formatDualCurrency(item.tax_vnd, item.tax_usd, filters.amountCurrency, item.record_type)}
              </td>
              <td className="report-amount-column">
                {formatDualCurrency(item.amount_vnd + item.tax_vnd, item.amount_usd + item.tax_usd, filters.amountCurrency, item.record_type)}
              </td>
              <td className="actions-column">
                <Button
                  variant="outline-info"
                  size="sm"
                  title="View Details"
                  onClick={() => handleViewDetails(item.month)}
                >
                  <FontAwesomeIcon icon={faInfoCircle} /> Details
                </Button>
              </td>
        </tr>
      ));
      
      rows = [...rows, ...monthRows];
    });
    
    return rows;
  };

  // Force refresh balance
  const forceRefreshBalance = () => {
    fetchCurrentBalance(true);
  };

  return (
    <div className="records-container">
      {/* Professional Header Panel */}
      <div className="records-header-panel">
        {/* Current Balance Section */}
        <div className="header-section balance-section">
          <div className="section-header">
            <div>
              <FontAwesomeIcon icon={faWallet} />
              <span>Current Balance</span>
            </div>
            {!balanceLoading && (
              <div className="balance-status">
                <OverlayTrigger
                  placement="top"
                  overlay={<BootstrapTooltip>Refresh balance</BootstrapTooltip>}
                >
                  <Button 
                    variant="link"
                    className="refresh-balance-btn" 
                    onClick={forceRefreshBalance}
                    disabled={balanceLoading}
                    size="sm"
                  >
                    <FontAwesomeIcon icon={faSync} />
                  </Button>
                </OverlayTrigger>
              </div>
            )}
          </div>
          <div className="balance-display no-gap">
            {balanceLoading ? (
              <div className="balance-loading">
                <div className="balance-primary">Loading...</div>
                <div className="balance-secondary">Please wait...</div>
              </div>
            ) : (
              <>
                <div className="balance-primary">{formatCurrency(currentBalance.total_vnd || 0, 'vnd')}</div>
                <div className="balance-secondary">{formatCurrency(currentBalance.total_usd || 0, 'usd')}</div>
              </>
            )}
          </div>
        </div>

        {/* Account Type Section */}
        <div className="header-section controls-section">
          <div className="control-label">
            <FontAwesomeIcon icon={faBuilding} />
            <span>Account</span>
          </div>
          <div className="radio-group">
            <label className="radio-option">
              <input 
                type="radio" 
                name="accountType" 
                value="company"
                checked={filters.accountType === 'company'}
                onChange={() => handleFilterChange({ accountType: 'company' })}
                className="radio-input"
              />
              <span>Company</span>
            </label>
            <label className="radio-option radio-option-small">
              <input 
                type="radio" 
                name="accountType" 
                value="personal" 
                checked={filters.accountType === 'personal'}
                onChange={() => handleFilterChange({ accountType: 'personal' })}
                className="radio-input"
              />
              <span>Personal</span>
            </label>
          </div>
        </div>

        {/* Exchange Rate Section */}
        <div className="header-section exchange-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faExchangeAlt} />
            <span>Exchange Rate</span>
            <span className="rate-label">VND/USD</span>
          </div>
          <div className="exchange-rate-container">
            <input 
              type="text" 
              value="26,100"
              readOnly
              className="rate-input"
              title="VND to USD exchange rate"
            />
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="header-section actions-section">
          <div className="actions-controls">
            <Dropdown>
              <Dropdown.Toggle 
                variant="primary" 
                id="quick-actions-dropdown"
                className="quick-actions-btn"
              >
                <FontAwesomeIcon icon={faBolt} className="quick-actions-btn-icon" />
                Quick Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item disabled className="text-muted small">
                  Actions coming soon...
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Filters */}
      <MonthlySummaryFilters
        filters={filters}
        categories={categories}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        onResetFilters={handleResetFilters}
      />

      {/* Monthly Summary Table */}
      <div className="records-table-container">
        {loading ? (
          <div className="loading-indicator">Loading...</div>
        ) : monthlyData.length === 0 ? (
          <div className="no-records">No data found for the selected filters.</div>
        ) : (
          <Table striped={false} bordered hover responsive className="records-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Category</th>
                <th>Type</th>
                <th className="amount-column">Amount</th>
                <th className="tax-column">Tax</th>
                <th className="report-amount-column">Report Amount</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {renderTableRows()}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default MonthlySummary;
